import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Visibility,
  CheckCircle,
  Cancel,
  Pending,
  FilterList,
  Download,
  Refresh,
  CalendarToday,
  Person,
  Business,
  AccessTime,
  Search,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
// Removed problematic MUI date picker imports
import { LEAVE_STATUS, LEAVE_TYPES } from '../../constants';
import { TableShimmer } from '../../components/common/Shimmer';
import leaveService from '../../services/leaveService';
import { useAuth } from '../../hooks/useAuth';
import ProfileAvatar from '../../components/common/ProfileAvatar';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`leave-tabpanel-${index}`}
      aria-labelledby={`leave-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const leaveSchema = yup.object({
  leave_type: yup.string().required('Leave type is required'),
  start_date: yup.date().required('Start date is required'),
  end_date: yup.date().required('End date is required'),
  reason: yup.string().required('Reason is required').min(10, 'Reason must be at least 10 characters'),
  is_emergency: yup.boolean(),
});

const LeavesPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [filteredLeaves, setFilteredLeaves] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<any>(null);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalComments, setApprovalComments] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    leave_type: 'all',
    store_id: 'all',
    cluster_id: 'all',
    start_date: '',
    end_date: '',
    is_emergency: 'all',
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(leaveSchema),
    defaultValues: {
      leave_type: '',
      start_date: undefined,
      end_date: undefined,
      reason: '',
      is_emergency: false,
    },
  });

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await leaveService.getLeaves();
      setLeaves(response.leaves || []);
      setFilteredLeaves(response.leaves || []);
      
      // Calculate stats
      const statsData = {
        total: response.leaves?.length || 0,
        pending: response.leaves?.filter((l: any) => l.status === 'pending').length || 0,
        approved: response.leaves?.filter((l: any) => l.status === 'approved').length || 0,
        rejected: response.leaves?.filter((l: any) => l.status === 'rejected').length || 0,
        cancelled: response.leaves?.filter((l: any) => l.status === 'cancelled').length || 0,
      };
      setStats(statsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch leaves');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, leaves]);

  const applyFilters = () => {
    let filtered = [...leaves];

    if (filters.status !== 'all') {
      filtered = filtered.filter(leave => leave.status === filters.status);
    }

    if (filters.leave_type !== 'all') {
      filtered = filtered.filter(leave => leave.leave_type === filters.leave_type);
    }

    if (filters.store_id !== 'all') {
      filtered = filtered.filter(leave => leave.store_id === filters.store_id);
    }

    if (filters.cluster_id !== 'all') {
      filtered = filtered.filter(leave => leave.cluster_id === filters.cluster_id);
    }

    if (filters.is_emergency !== 'all') {
      const isEmergency = filters.is_emergency === 'true';
      filtered = filtered.filter(leave => leave.is_emergency === isEmergency);
    }

    if (filters.start_date) {
      filtered = filtered.filter(leave => 
        new Date(leave.start_date) >= new Date(filters.start_date)
      );
    }

    if (filters.end_date) {
      filtered = filtered.filter(leave => 
        new Date(leave.end_date) <= new Date(filters.end_date)
      );
    }

    setFilteredLeaves(filtered);
  };

  const handleCreateLeave = async (data: any) => {
    try {
      // Add required fields from current user
      const leaveData = {
        ...data,
        store_id: user?.store_id,
        cluster_id: user?.cluster_id,
      };
      
      console.log('Creating leave with data:', leaveData);
      
      // Validate required fields
      if (!leaveData.store_id || !leaveData.cluster_id) {
        setError('User must be assigned to a store and cluster to apply for leave');
        return;
      }
      
      await leaveService.createLeave(leaveData);
      setOpenDialog(false);
      reset();
      fetchLeaves();
    } catch (err: any) {
      console.error('Create leave error:', err);
      setError(err.response?.data?.message || 'Failed to create leave request');
    }
  };

  const handleApproveLeave = async (leaveId: string, action: 'approve' | 'reject') => {
    try {
      const approvalData: any = { status: action === 'approve' ? 'approved' : 'rejected' };
      
      if (action === 'reject' && rejectionReason.trim()) {
        approvalData.rejection_reason = rejectionReason.trim();
      }
      
      if (action === 'approve' && approvalComments.trim()) {
        approvalData.comments = approvalComments.trim();
      }
      
      await leaveService.approveLeave(leaveId, approvalData);
      setOpenApproveDialog(false);
      setSelectedLeave(null);
      setRejectionReason('');
      setApprovalComments('');
      fetchLeaves();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update leave request');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Pending />;
      case 'approved':
        return <CheckCircle />;
      case 'rejected':
        return <Cancel />;
      case 'cancelled':
        return <Cancel />;
      default:
        return <Pending />;
    }
  };

  const getLeaveTypeColor = (type: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      sick_leave: 'error',
      casual_leave: 'info',
      earned_leave: 'success',
      maternity_leave: 'secondary',
      paternity_leave: 'secondary',
      emergency_leave: 'warning',
    };
    return colors[type] || 'default';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const calculateDays = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Leave Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Manage leave requests and approvals
          </Typography>
        </Box>
        <TableShimmer rows={8} columns={8} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Action Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search leaves..."
            value={filters.search || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: '#6c757d', fontSize: 18 }} />,
            }}
            sx={{ 
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#f8f9fa',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                },
              }
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => {/* Export functionality */}}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
              },
            }}
          >
            Apply Leave
          </Button>
        </Box>
      </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ backgroundColor: '#6c757d', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <CalendarToday />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                    Total Leaves
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ backgroundColor: '#ff9800', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <Pending />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                    Pending
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                  {stats.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ backgroundColor: '#4caf50', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <CheckCircle />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                    Approved
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                  {stats.approved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ backgroundColor: '#f44336', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <Cancel />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                    Rejected
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                  {stats.rejected}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ backgroundColor: '#9e9e9e', color: 'white', borderRadius: 2, p: 1.5, mr: 2 }}>
                    <Cancel />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#333333', fontWeight: 500 }}>
                    Cancelled
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                  {stats.cancelled}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterList sx={{ mr: 1, color: '#6c757d' }} />
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Leave Type</InputLabel>
                <Select
                  value={filters.leave_type}
                  label="Leave Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, leave_type: e.target.value }))}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="sick_leave">Sick Leave</MenuItem>
                  <MenuItem value="casual_leave">Casual Leave</MenuItem>
                  <MenuItem value="earned_leave">Earned Leave</MenuItem>
                  <MenuItem value="maternity_leave">Maternity Leave</MenuItem>
                  <MenuItem value="paternity_leave">Paternity Leave</MenuItem>
                  <MenuItem value="emergency_leave">Emergency Leave</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Emergency</InputLabel>
                <Select
                  value={filters.is_emergency}
                  label="Emergency"
                  onChange={(e) => setFilters(prev => ({ ...prev, is_emergency: e.target.value }))}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Emergency Only</MenuItem>
                  <MenuItem value="false">Regular Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => setFilters({
                  search: '',
                  status: 'all',
                  leave_type: 'all',
                  store_id: 'all',
                  cluster_id: 'all',
                  start_date: '',
                  end_date: '',
                  is_emergency: 'all',
                })}
                fullWidth
                size="small"
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Leaves Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Employee ID</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>Cluster</TableCell>
                <TableCell>Leave Type</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Days</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Applied Date</TableCell>
                <TableCell>Emergency</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLeaves.map((leave) => (
                <TableRow key={leave.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ProfileAvatar
                        imageUrl={leave.user?.profile_image_url}
                        firstName={leave.user?.first_name}
                        lastName={leave.user?.last_name}
                        sx={{ bgcolor: '#6c757d', width: 32, height: 32 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {leave.user?.first_name} {leave.user?.last_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666666' }}>
                          {leave.user?.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {leave.user?.employee_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {leave.store?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {leave.cluster?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={leave.leave_type?.replace('_', ' ').toUpperCase()}
                      color={getLeaveTypeColor(leave.leave_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {leave.total_days} days
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(leave.status)}
                      label={leave.status?.toUpperCase()}
                      color={getStatusColor(leave.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(leave.applied_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {leave.is_emergency ? (
                      <Chip label="Emergency" color="error" size="small" />
                    ) : (
                      <Typography variant="body2" sx={{ color: '#666666' }}>
                        Regular
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLeave(leave);
                            setOpenViewDialog(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {leave.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setApprovalAction('approve');
                                setOpenApproveDialog(true);
                              }}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setApprovalAction('reject');
                                setOpenApproveDialog(true);
                              }}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Leave Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Apply for Leave</DialogTitle>
          <form onSubmit={handleSubmit(handleCreateLeave)}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Leave Type</InputLabel>
                    <Select
                      {...register('leave_type')}
                      label="Leave Type"
                      error={!!errors.leave_type}
                    >
                      <MenuItem value="sick_leave">Sick Leave</MenuItem>
                      <MenuItem value="casual_leave">Casual Leave</MenuItem>
                      <MenuItem value="earned_leave">Earned Leave</MenuItem>
                      <MenuItem value="maternity_leave">Maternity Leave</MenuItem>
                      <MenuItem value="paternity_leave">Paternity Leave</MenuItem>
                      <MenuItem value="emergency_leave">Emergency Leave</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch {...register('is_emergency')} />}
                    label="Emergency Leave"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('start_date')}
                    type="date"
                    label="Start Date"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.start_date}
                    helperText={errors.start_date?.message}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('end_date')}
                    type="date"
                    label="End Date"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.end_date}
                    helperText={errors.end_date?.message}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    {...register('reason')}
                    fullWidth
                    multiline
                    rows={4}
                    label="Reason"
                    margin="normal"
                    error={!!errors.reason}
                    helperText={errors.reason?.message}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button type="submit" variant="contained">Apply Leave</Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* View Leave Dialog */}
        <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Leave Details</DialogTitle>
          <DialogContent>
            {selectedLeave && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                  <Typography variant="body1">{selectedLeave.user?.first_name} {selectedLeave.user?.last_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Employee ID</Typography>
                  <Typography variant="body1">{selectedLeave.user?.employee_id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Leave Type</Typography>
                  <Typography variant="body1">{selectedLeave.leave_type?.replace('_', ' ').toUpperCase()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Duration</Typography>
                  <Typography variant="body1">{selectedLeave.total_days} days</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Start Date</Typography>
                  <Typography variant="body1">{formatDate(selectedLeave.start_date)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">End Date</Typography>
                  <Typography variant="body1">{formatDate(selectedLeave.end_date)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedLeave.status)}
                    label={selectedLeave.status?.toUpperCase()}
                    color={getStatusColor(selectedLeave.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Emergency</Typography>
                  <Typography variant="body1">{selectedLeave.is_emergency ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Reason</Typography>
                  <Typography variant="body1">{selectedLeave.reason}</Typography>
                </Grid>
                {selectedLeave.comments && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Comments</Typography>
                    <Typography variant="body1">{selectedLeave.comments}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Approve/Reject Leave Dialog */}
        <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {approvalAction === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
          </DialogTitle>
          <DialogContent>
            {selectedLeave && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Employee: {selectedLeave.user?.first_name} {selectedLeave.user?.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Leave Type: {selectedLeave.leave_type?.replace('_', ' ').toUpperCase()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {selectedLeave.start_date} to {selectedLeave.end_date}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reason: {selectedLeave.reason}
                </Typography>
              </Box>
            )}
            
            {approvalAction === 'reject' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Reason *"
                margin="normal"
                placeholder="Please provide a reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                error={approvalAction === 'reject' && !rejectionReason.trim()}
                helperText={approvalAction === 'reject' && !rejectionReason.trim() ? 'Rejection reason is required' : ''}
              />
            )}
            
            {approvalAction === 'approve' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comments (Optional)"
                margin="normal"
                placeholder="Add any comments for the employee..."
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenApproveDialog(false);
              setRejectionReason('');
              setApprovalComments('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleApproveLeave(selectedLeave?.id, 'reject')}
              color="error"
              variant="contained"
              disabled={approvalAction === 'reject' && !rejectionReason.trim()}
            >
              Reject
            </Button>
            <Button
              onClick={() => handleApproveLeave(selectedLeave?.id, 'approve')}
              color="success"
              variant="contained"
            >
              Approve
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default LeavesPage;
